import React from 'react';
import {
  FieldGroup,
  FieldLabel,
  InlineError,
  RequiredMark,
  TextInput,
} from '../Onboarding.styled';

export interface LabeledTextInputProps {
  id: string;
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (next: string) => void;
  error?: string;
  dataTestId?: string;
  autoFocus?: boolean;
  /** When true, renders a red asterisk after the label. */
  required?: boolean;
}

/**
 * Label + TextInput + inline error. Onboarding-specific to keep the
 * low-contrast surface fill, 44px height, and tiny uppercase label out of
 * the global atom library.
 */
const LabeledTextInput: React.FC<LabeledTextInputProps> = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  error,
  dataTestId,
  autoFocus,
  required,
}) => {
  const testId = dataTestId ?? `onboarding-input-${id}`;
  const errorId = `${id}-error`;
  return (
    <FieldGroup>
      {label && (
        <FieldLabel htmlFor={id}>
          {label}
          {required && <RequiredMark aria-hidden="true">*</RequiredMark>}
        </FieldLabel>
      )}
      <TextInput
        id={id}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        // Tell password managers to leave these inputs alone — they inject
        // icons / dropdowns into recognised name/email fields which causes a
        // layout shift on focus. This is a one-time onboarding form, no
        // credentials, so we lose nothing by suppressing them.
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore=""
        data-bwignore="true"
        data-form-type="other"
      />
      {error && (
        <InlineError id={errorId} data-testid={`${testId}__error`} role="alert">
          {error}
        </InlineError>
      )}
    </FieldGroup>
  );
};

export default LabeledTextInput;
